"use client"

import * as React from "react"
import { format, parseISO } from "date-fns"
import type { OverviewRecentRowsTableRow } from "@ibada/overview-widgets"
import { OverviewMetricWidget, OverviewRecentRowsTable } from "@ibada/overview-widgets"

import { Badge, cn } from "@ibada/ui"
import { OverviewContext, OverviewTableColumnType, type OverviewChart, type OverviewTable } from "@ibada/shared-types/overview"

import { DashboardDivergingAreaChart } from "@/features/finance/overview/charts/dashboard-charts"
import { ProgressBar } from "@/features/finance/overview/components/progress-bar"
import { formatMoneySafe } from "@/features/finance/overview/lib/overview-format"
import { buildIncomeExpenseTrendRows } from "@/features/finance/lib/finance-overview-integration"
import { FinanceOverviewCardId } from "@/features/finance/model/finance-normalized-overview"
import {
  findDashboardRecentRecordsTable,
  RECENT_TRANSACTION_TITLE_KEYS,
} from "@/features/dashboard/components/dashboard-recent-records-card"
import { isIncomeLikeTransactionType } from "@/features/finance/components/transactions/transaction-direction-sign"
import type { DashboardLayoutStat } from "@/features/dashboard/model/dashboard-overview-layout"
import { useDashboardOverview } from "@/features/dashboard/overview/context/dashboard-overview-context"
import { useLocale, useTranslations } from "@/i18n"
import { createCurrencyFormatter } from "@/shared/lib/formatting/currency"
import { coerceNumber, readMoney } from "@/shared/lib/overview/overview-formatting"
import { useWidgetMenu } from "@ibada/dashboard-engine"

const MEMBER_MIX_COLORS = ["#10b981", "#4f6df5", "#f59e0b", "#ec4899", "#94a3b8"]
const INCOME_COLOR = "#10b981"
const EXPENSE_COLOR = "#f43f5e"

function useDashboardWidgetRefresh() {
  const { data } = useDashboardOverview()
  useWidgetMenu({
    refresh: () => {
      void data.refetch()
    },
  })
}

export function DashboardTotalMembersWidget() {
  const { data } = useDashboardOverview()
  return <DashboardMetricWidget stat={data.overviewLayout.memberStats.totalMembers} tone="text-foreground" />
}

export function DashboardActiveMembersWidget() {
  const { data } = useDashboardOverview()
  return <DashboardMetricWidget outOf={data.overviewLayout.memberStats.totalMembers} stat={data.overviewLayout.memberStats.activeMembers} tone="text-emerald-600 dark:text-emerald-400" />
}

export function DashboardNewMembersWidget() {
  const { data } = useDashboardOverview()
  return <DashboardMetricWidget outOf={data.overviewLayout.memberStats.totalMembers} stat={data.overviewLayout.memberStats.newMembers} tone="text-sky-600 dark:text-sky-400" />
}

export function DashboardVisitorsWidget() {
  const { data } = useDashboardOverview()
  return <DashboardMetricWidget outOf={data.overviewLayout.memberStats.totalMembers} stat={data.overviewLayout.memberStats.visitors} tone="text-violet-600 dark:text-violet-400" />
}

export function DashboardTotalIncomeWidget() {
  const { data } = useDashboardOverview()
  return (
    <DashboardMetricWidget
      stat={findFinanceStat(data.overviewLayout.financeSummary, FinanceOverviewCardId.TotalIncome)}
      tone="text-emerald-600 dark:text-emerald-400"
    />
  )
}

export function DashboardTotalExpensesWidget() {
  const { data } = useDashboardOverview()
  return (
    <DashboardMetricWidget
      stat={findFinanceStat(data.overviewLayout.financeSummary, FinanceOverviewCardId.TotalExpenses)}
      tone="text-rose-600 dark:text-rose-400"
    />
  )
}

export function DashboardNetBalanceWidget() {
  const { data } = useDashboardOverview()
  const stat = findFinanceStat(data.overviewLayout.financeSummary, FinanceOverviewCardId.NetBalance)
  return <DashboardMetricWidget stat={stat} tone={stat && stat.rawValue < 0 ? "text-rose-600 dark:text-rose-400" : "text-foreground"} />
}

export function DashboardTransfersOutWidget() {
  const { data } = useDashboardOverview()
  // Not red: money remitted to the parish is not money lost, and colouring it like a cost is the whole
  // mistake this card exists to undo.
  return (
    <DashboardMetricWidget
      stat={findFinanceStat(data.overviewLayout.financeSummary, FinanceOverviewCardId.TransfersOut)}
      tone="text-foreground"
    />
  )
}

export function DashboardCashOnHandWidget() {
  const { data } = useDashboardOverview()
  return (
    <DashboardMetricWidget
      stat={findFinanceStat(data.overviewLayout.financeSummary, FinanceOverviewCardId.CashOnHand)}
      tone="text-foreground"
    />
  )
}

export function DashboardPendingApprovalsWidget() {
  const { data } = useDashboardOverview()
  return <DashboardMetricWidget stat={data.overviewLayout.activityStats.pendingApprovals} tone="text-amber-600 dark:text-amber-400" />
}

export function DashboardActiveActivitiesWidget() {
  const { data } = useDashboardOverview()
  return <DashboardMetricWidget stat={data.overviewLayout.activityStats.activeActivities} tone="text-sky-600 dark:text-sky-400" />
}

export function DashboardTransactionCountWidget() {
  const { data } = useDashboardOverview()
  return <DashboardMetricWidget stat={data.overviewLayout.activityStats.transactionCount} tone="text-foreground" />
}

function findFinanceStat(stats: DashboardLayoutStat[], key: FinanceOverviewCardId): DashboardLayoutStat | null {
  return stats.find((stat) => stat.key === key) ?? null
}

function DashboardMetricWidget({
  stat,
  tone,
  outOf,
}: {
  stat: DashboardLayoutStat | null
  tone: string
  /**
   * The denominator this figure is a share OF, when there honestly is one. Omitted deliberately where none
   * exists — a total is not a share of anything, and pending approvals have nothing to be a percentage of.
   */
  outOf?: DashboardLayoutStat | null
}) {
  useDashboardWidgetRefresh()
  if (!stat) return null

  const total = outOf?.rawValue ?? 0
  // Guarded on the denominator, not the numerator: 0 of 780 is a meaningful 0%, but anything of 0 is not.
  const share = total > 0 ? Math.round((stat.rawValue / total) * 100) : null

  // The markup lives in @ibada/overview-widgets so the admin platform board renders identically — the two
  // boards are the same product idea at different scopes and must not drift apart.
  return (
    <OverviewMetricWidget
      value={stat.value}
      tone={tone}
      sharePercentage={share}
      helper={stat.helper || stat.label}
    />
  )
}

function rowNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0
}

export function DashboardFinanceTrendWidget() {
  const { data, overviewMode } = useDashboardOverview()
  const t = useTranslations("dashboard.admin")
  useDashboardWidgetRefresh()

  const chart = data.overviewLayout.trendChart
  if (overviewMode !== "branch" || !chart) return null

  const rows = buildIncomeExpenseTrendRows(chart)
    .map((row): Record<string, string | number> => {
      const income = Math.max(0, rowNumber(row.income))
      const expense = Math.max(0, Math.abs(rowNumber(row.expense)))
      return { ...row, income, expense: expense === 0 ? 0 : -expense }
    })
    .filter((row) => rowNumber(row.income) !== 0 || rowNumber(row.expense) !== 0)
  if (rows.length === 0) return null

  const totals = rows.reduce<{ income: number; expense: number }>(
    (sum, row) => ({
      income: sum.income + rowNumber(row.income),
      expense: sum.expense + Math.abs(rowNumber(row.expense)),
    }),
    { income: 0, expense: 0 },
  )
  const net = totals.income - totals.expense

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <TrendMetric label={t("widgets.financeTrend.income")} value={formatMoneySafe(totals.income, data.overviewCurrencyCode)} tone="text-emerald-600 dark:text-emerald-400" />
        <TrendMetric label={t("widgets.financeTrend.expense")} value={formatMoneySafe(totals.expense, data.overviewCurrencyCode)} tone="text-rose-600 dark:text-rose-400" />
        <TrendMetric label={t("widgets.financeTrend.net")} value={formatMoneySafe(net, data.overviewCurrencyCode)} tone="text-foreground" />
      </div>
      <DashboardDivergingAreaChart
        data={rows}
        xKey="period"
        currency={data.overviewCurrencyCode}
        height={170}
        positiveSeries={{ key: "income", label: t("widgets.financeTrend.income"), color: INCOME_COLOR }}
        negativeSeries={{ key: "expense", label: t("widgets.financeTrend.expense"), color: EXPENSE_COLOR }}
      />
    </div>
  )
}

function TrendMetric({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div className="min-w-0 rounded-md bg-muted/25 px-2 py-1.5">
      <p className="truncate text-[11px] text-muted-foreground">{label}</p>
      <p className={cn("truncate text-sm font-semibold tabular-nums", tone)}>{value}</p>
    </div>
  )
}

export function DashboardMemberMixWidget() {
  const t = useTranslations("dashboard.admin")
  const { data } = useDashboardOverview()
  useDashboardWidgetRefresh()

  if (!data.visibility.showCharts) return null
  const rows = data.overviewLayout.memberDistribution.rows.slice(0, 6)
  if (rows.length === 0) return null

  return <DistributionList rows={rows} totalLabel={t("total")} />
}

function DistributionList({
  rows,
  totalLabel,
}: {
  rows: Array<{ key: string; label: string; value: number; percentage: number }>
  totalLabel?: string
}) {
  const locale = useLocale()
  const formatter = React.useMemo(() => new Intl.NumberFormat(locale), [locale])
  const total = rows.reduce((sum, row) => sum + row.value, 0)

  return (
    <div className="space-y-2.5">
      <div className="flex items-end justify-between gap-3">
        <p className="text-2xl font-semibold tabular-nums tracking-tight">{formatter.format(total)}</p>
        {totalLabel ? <p className="pb-0.5 text-xs text-muted-foreground">{totalLabel}</p> : null}
      </div>
      <ul className="space-y-2">
        {rows.map((row, index) => (
          <li key={row.key} className="space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ backgroundColor: MEMBER_MIX_COLORS[index % MEMBER_MIX_COLORS.length] }}
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1 truncate">{row.label}</span>
              <span className="shrink-0 font-medium tabular-nums">{formatter.format(row.value)}</span>
              <span className="w-9 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{row.percentage}%</span>
            </div>
            <ProgressBar value={row.percentage} className="h-1" />
          </li>
        ))}
      </ul>
    </div>
  )
}

export function DashboardMemberGrowthWidget() {
  const { data } = useDashboardOverview()
  const t = useTranslations("dashboard.membersGrowthCard")
  useDashboardWidgetRefresh()

  if (!data.visibility.showCharts) return null
  if (data.membersGrowth.state === "access_denied") return <WidgetPlaceholder message={t("locked")} />
  if (data.membersGrowth.data.length === 0) return <WidgetPlaceholder message={t("empty")} />

  const points = data.membersGrowth.data
  const first = points[0]
  const last = points.at(-1)
  const delta = first && last ? last.members - first.members : 0

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-2xl font-semibold tabular-nums tracking-tight">{last?.members.toLocaleString() ?? "0"}</p>
          <p className="truncate text-xs text-muted-foreground">{last?.month ?? ""}</p>
        </div>
        <Badge variant="outline" className={cn("shrink-0 text-xs", delta >= 0 ? "text-emerald-600" : "text-rose-600")}>
          {delta >= 0 ? "+" : ""}
          {delta.toLocaleString()}
        </Badge>
      </div>
      <MemberGrowthSparkline points={points} />
    </div>
  )
}

function MemberGrowthSparkline({ points }: { points: Array<{ month: string; members: number }> }) {
  const values = points.map((point) => point.members)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = Math.max(max - min, 1)
  const width = 220
  const height = 94
  const coordinates = points.map((point, index) => {
    const x = points.length === 1 ? width / 2 : (index / (points.length - 1)) * width
    const y = height - ((point.members - min) / range) * (height - 12) - 6
    return { x, y }
  })
  const line = coordinates.map((point) => `${point.x},${point.y}`).join(" ")
  const area = coordinates.length > 0 ? `0,${height} ${line} ${width},${height}` : ""

  return (
    <div className="overflow-hidden rounded-lg border border-border/50 bg-muted/15 px-2 py-2">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-24 w-full" role="img" aria-hidden="true" preserveAspectRatio="none">
        <defs>
          <linearGradient id="dashboard-member-growth-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgb(16 185 129)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="rgb(16 185 129)" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <polygon points={area} fill="url(#dashboard-member-growth-fill)" stroke="none" />
        <polyline points={line} fill="none" stroke="rgb(16 185 129)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

function WidgetPlaceholder({ message }: { message: string }) {
  return (
    <div className="flex min-h-32 items-center justify-center rounded-lg border border-dashed border-border/70 px-4 py-6 text-center text-sm text-muted-foreground">
      {message}
    </div>
  )
}

function toDistributionRows(chart: OverviewChart | null, labelFor: (id: string) => string) {
  const segments = chart?.segments ?? []
  const total = segments.reduce((sum, segment) => sum + segment.value, 0)
  return segments.map((segment, index) => {
    const id = segment.id ?? ""
    return {
      key: id || `${segment.labelKey}-${index}`,
      label: labelFor(id || "unknown"),
      value: segment.value,
      percentage: total > 0 ? Math.round((segment.value / total) * 100) : 0,
    }
  })
}

function useParishInsights() {
  const { data } = useDashboardOverview()
  const t = useTranslations("dashboard.admin")
  const isParishOverview = data.mergedOverview?.meta.context === OverviewContext.Parish
  return { data, t, isParishOverview }
}

export function DashboardParishGenderInsightsWidget() {
  const { data, t, isParishOverview } = useParishInsights()
  useDashboardWidgetRefresh()

  if (!data.visibility.showCharts || !isParishOverview) return null
  const rows = toDistributionRows(data.overviewLayout.parishInsights.genderChart, (id) => t(`parishInsights.gender.${id}`))
  if (rows.length === 0) return null

  return <DistributionList rows={rows} />
}

export function DashboardParishAgeInsightsWidget() {
  const { data, t, isParishOverview } = useParishInsights()
  useDashboardWidgetRefresh()

  if (!data.visibility.showCharts || !isParishOverview) return null
  const rows = toDistributionRows(data.overviewLayout.parishInsights.ageChart, (id) => t(`parishInsights.age.${id}`))
  if (rows.length === 0) return null

  return <DistributionList rows={rows} />
}

export function DashboardParishBranchDistributionWidget() {
  const { data, isParishOverview } = useParishInsights()
  useDashboardWidgetRefresh()

  if (!data.visibility.showCharts || !isParishOverview) return null

  const chart = data.overviewLayout.parishInsights.branchChart
  const series = chart?.series[0]
  const rawRows =
    chart && series
      ? chart.labels.map((label, index) => ({
          key: `${label}-${index}`,
          label,
          value: series.data[index] ?? 0,
        }))
      : []
  const total = rawRows.reduce((sum, row) => sum + row.value, 0)
  const rows = rawRows
    .map((row) => ({ ...row, percentage: total > 0 ? Math.round((row.value / total) * 100) : 0 }))
    .sort((left, right) => right.value - left.value)
    .slice(0, 7)
  if (rows.length === 0) return null

  return <DistributionList rows={rows} />
}

export function DashboardRecentRecordsWidget() {
  const { data } = useDashboardOverview()
  const locale = useLocale()
  useDashboardWidgetRefresh()

  const table = findDashboardRecentRecordsTable(data.mergedOverview)
  const rows = React.useMemo(() => buildRecentRows(table, data.overviewCurrencyCode, locale), [data.overviewCurrencyCode, locale, table])
  if (!table || rows.length === 0) return null

  return <OverviewRecentRowsTable rows={rows} />
}

function buildRecentRows(table: OverviewTable | null, currencyCode: string, locale: string): OverviewRecentRowsTableRow[] {
  if (!table) return []
  return table.rows.map((row, index) => {
    if (table.id === "recentTransactions") {
      const amount = formatTableValue(table, row, "amount", currencyCode, locale)
      // The API sends the enum value ("INCOME", "TRANSFER_IN"), never lower-case "income".
      const isIncome = isIncomeLikeTransactionType(row.type)
      return {
        id: getString(row, ["id"]) ?? `${table.id}-${index}`,
        dateLabel: formatRecentDate(formatTableValue(table, row, "occurredAt", currencyCode, locale)),
        label: getString(row, RECENT_TRANSACTION_TITLE_KEYS) ?? "—",
        valueLabel: `${isIncome ? "+" : "−"}${amount}`,
        valueTone: isIncome ? "income" : "expense",
      }
    }

    const firstName = getString(row, ["firstName"]) ?? ""
    const lastName = getString(row, ["lastName"]) ?? ""
    const name = `${firstName} ${lastName}`.trim()
    return {
      id: getString(row, ["id"]) ?? `${table.id}-${index}`,
      dateLabel: formatRecentDate(formatTableValue(table, row, "createdAt", currencyCode, locale) || formatTableValue(table, row, "startsAt", currencyCode, locale)),
      label: name || getString(row, ["title", "name", "assetName", "email"]) || "—",
      valueLabel: getString(row, ["branchName", "category", "categoryName", "condition", "responsibleMemberName", "status"]) ?? "—",
      valueTone: "muted",
    }
  })
}

function formatRecentDate(value: string): string {
  if (!value) return "—"
  try {
    return format(parseISO(value), "d MMM")
  } catch {
    return value
  }
}

function getString(row: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === "string" && value.trim().length > 0) return value.trim()
  }
  return null
}

function formatTableValue(table: OverviewTable, row: Record<string, unknown>, columnKey: string, currencyCode: string, locale: string): string {
  const column = table.columns.find((item) => item.key === columnKey)
  if (!column) return ""
  const raw = row[column.key]
  if (raw === null || raw === undefined || raw === "") return ""
  if (column.columnType === OverviewTableColumnType.Money) {
    // A canonical decimal string (IBA-116), handed to `Intl` exactly as it arrived.
    const amount = readMoney(raw)
    return amount === null ? String(raw) : createCurrencyFormatter(currencyCode, locale).format(amount)
  }
  if (column.columnType === OverviewTableColumnType.Number) {
    const value = coerceNumber(raw)
    return value === null ? String(raw) : value.toLocaleString(locale, { maximumFractionDigits: 2 })
  }
  if (column.columnType === OverviewTableColumnType.Date) return String(raw).slice(0, 10)
  return String(raw)
}

export function DashboardScopeNoteWidget() {
  const { data } = useDashboardOverview()
  const note = data.overviewLayout.scopeNote
  if (!note) return null

  return <p className="text-xs leading-relaxed text-muted-foreground">{note}</p>
}
